import { useEffect, useRef } from 'react';
import { ILayerProps } from '@/version-types';
import { CoreAPI, useIdentity } from '../../core';
import { StageAPI, useActiveOriginIndex, useIsPickStage } from '../../stage';
import { useActiveInfoKey } from '../hooks';
import { getLayerOperateState } from '../states/layer-operate-state';

export const ActiveListener = () => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const activeInfoKey = useActiveInfoKey();
    const preData = useRef<{
        layer: ILayerProps | null;
        pickLayer: ILayerProps | null;
    } | null>(null);
    const preEventLayerId = useRef<string | null | undefined>(null);

    function emitActiveUpdate(
        currentData: {
            layer: ILayerProps | null;
            pickLayer: ILayerProps | null;
        } | null,
    ) {
        if (
            currentData &&
            (currentData.layer !== preData.current?.layer || currentData.pickLayer !== preData.current?.pickLayer)
        ) {
            preData.current = currentData;
            coreApi.events.emitActiveLayerDataUpdate(currentData.layer, currentData.pickLayer);
        }
        preEventLayerId.current = currentData?.layer?.id;
        return currentData;
    }

    useEffect(() => {
        const operate = getLayerOperateState(identity);
        const layer = operate.getActiveLayer();
        let pickLayer = operate.getActivePickLayer();
        pickLayer = layer === pickLayer ? null : pickLayer;
        const currentData = { layer, pickLayer };
        emitActiveUpdate(currentData);

        const activeId = layer?.id;
        if (!activeId) return;

        const handler = coreApi.events.onRerenderLayer(activeId, (layerId) => {
            if (preEventLayerId.current === layerId) {
                preEventLayerId.current = null;
                return;
            }
            const pickLayer = operate.getActivePickLayer();
            let currentData: {
                layer: ILayerProps | null;
                pickLayer: ILayerProps | null;
            } | null = null;
            if (pickLayer?.id === layerId) {
                currentData = { layer: pickLayer, pickLayer: null };
            } else if (pickLayer) {
                currentData = { layer: preData.current?.layer ?? null, pickLayer };
            }
            emitActiveUpdate(currentData);
        });
        return () => {
            coreApi.events.off(handler);
        };
    }, [activeInfoKey]);

    return null;
};

export const ActiveStageListener = () => {
    const originIndex = useActiveOriginIndex();
    const timer = useRef<NodeJS.Timeout | null>(null);
    const identity = useIdentity();
    useEffect(() => {
        const coreApi = CoreAPI(identity);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            coreApi.events.emitActiveStageChange({
                stageIndex: coreApi.convertOriginToStage(originIndex),
                originIndex,
            });
        }, 0);
        const stageApi = StageAPI(identity);
        if (!stageApi.isLongImageMode() && !stageApi.getIsAutoScale()) {
            // 切换画布重新设置自动缩放
            stageApi.setIsAutoScale(true);
        }
    }, [originIndex]);

    return null;
};

/**
 * 监听选中舞台
 */
export const PickStageListener = () => {
    const isPickStage = useIsPickStage();
    const identity = useIdentity();

    useEffect(() => {
        const coreApi = CoreAPI(identity);
        coreApi.events.emitPickStageChange(isPickStage);
    }, [isPickStage]);

    return null;
};
